import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { GetOrdersParams, getOrdersCount } from '@apis/order';
import { User, UserGroup } from '@models/user';
import { dropEmptyValues } from '../../utils/common';

/**
 * Orders count query.
 *
 * Notes:
 * - Cache key must include all filters that affect the count.
 * - Partner users are always scoped to their own partnerId.
 */
export const useOrdersCount = (params: GetOrdersParams, user: User | null) => {
  const isPartner = user?.group === UserGroup.PARTNER;

  /**
   * Normalize request params:
   * - Remove empty values for stable caching and consistent requests.
   * - Force partnerId for Partner users.
   */
  const normalizedParams: GetOrdersParams = {
    ...dropEmptyValues(params, [
      'orderCode',
      'productCode',
      'productName',
      'stockId',
      'partnerId',
      'orderStatus',
      'createdAtFrom',
      'createdAtTo',
      'reservedAtFrom',
      'reservedAtTo',
      'iccid',
      'passportNumber',
      'requestOrderId',
      'externalOrderId',
      'productSaleType',
    ]),
    ...(isPartner && user?.partnerId ? { partnerId: user.partnerId } : {}),
  };

  const queryOptions: UseQueryOptions<number, Error> = {
    /**
     * Cache key must include filters; otherwise count can stay stale until refresh.
     */
    queryKey: ['ordersCount', user?.userId, isPartner ? user?.partnerId : null, normalizedParams],

    queryFn: () => getOrdersCount(normalizedParams),

    enabled: Boolean(user) && (isPartner ? Boolean(user?.partnerId) : true),

    refetchOnMount: true,
  };

  return useQuery(queryOptions);
};
